import { useState } from 'react'
import { useTranslation } from '@kostbar/i18n'
import { useProductDetail } from '../hooks/use-product-detail'
import { ProductInfoTab } from './product-info-tab'
import { AssignmentsTab } from './assignments-tab'
import { PricesTab } from './prices-tab'
import { VolumePricingTab } from './volume-pricing-tab'
import { InventoryTab } from './inventory-tab'
import { ProductImagesTab } from './product-images-tab'
import { ProductMetaTab } from './product-meta-tab'
import { StockUsageTab } from './stock-usage-tab'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

interface ProductDetailTabsProps {
  sku: string
  /** The tab to open first; "info" unless a deep link names another. */
  initialTab?: 'stockUsage'
}

export function ProductDetailTabs({ sku, initialTab }: ProductDetailTabsProps) {
  const { t: tProducts } = useTranslation('products')
  const { data: product } = useProductDetail(sku)
  const [tab, setTab] = useState<string>(initialTab ?? 'info')
  // CHG-2026-09-stock-usage-and-price-twins (10.6): a variant takes its images, metadata and stock
  // usage from its source and holds no stock of its own, so those tabs are not offered; its
  // "Lagerverbrauch" tab stays visible but disabled, pointing at the source.
  const isVariant = !!product?.variantSourceSku
  const hiddenForVariant = new Set([
    'inventory',
    'stockUsage',
    'images',
    'metadata',
  ])
  const activeTab = isVariant && hiddenForVariant.has(tab) ? 'info' : tab

  return (
    <Tabs value={activeTab} onValueChange={setTab} className="w-full">
      <TabsList>
        <TabsTrigger value="info">{tProducts('tabs.info')}</TabsTrigger>
        <TabsTrigger value="assignments">
          {tProducts('tabs.assignments')}
        </TabsTrigger>
        <TabsTrigger value="prices">{tProducts('tabs.prices')}</TabsTrigger>
        <TabsTrigger value="volumePricing">
          {tProducts('tabs.volumePricing')}
        </TabsTrigger>
        {!isVariant && (
          <TabsTrigger value="inventory">
            {tProducts('tabs.inventory')}
          </TabsTrigger>
        )}
        <TabsTrigger value="stockUsage" disabled={isVariant}>
          {isVariant
            ? tProducts('tabs.stockUsageFromSource')
            : tProducts('tabs.stockUsage')}
        </TabsTrigger>
        {!isVariant && (
          <TabsTrigger value="images">{tProducts('tabs.images')}</TabsTrigger>
        )}
        {!isVariant && (
          <TabsTrigger value="metadata">
            {tProducts('tabs.metadata')}
          </TabsTrigger>
        )}
      </TabsList>

      <TabsContent value="info" className="mt-6">
        <ProductInfoTab sku={sku} />
      </TabsContent>

      <TabsContent value="assignments" className="mt-6">
        <AssignmentsTab sku={sku} />
      </TabsContent>

      <TabsContent value="prices" className="mt-6">
        <PricesTab sku={sku} />
      </TabsContent>

      <TabsContent value="volumePricing" className="mt-6">
        <VolumePricingTab sku={sku} />
      </TabsContent>

      {!isVariant && (
        <TabsContent value="inventory" className="mt-6">
          <InventoryTab sku={sku} />
        </TabsContent>
      )}

      {!isVariant && (
        <TabsContent value="stockUsage" className="mt-6">
          <StockUsageTab sku={sku} onOpenInfoTab={() => setTab('info')} />
        </TabsContent>
      )}

      {!isVariant && (
        <TabsContent value="images" className="mt-6">
          <ProductImagesTab sku={sku} />
        </TabsContent>
      )}

      {!isVariant && (
        <TabsContent value="metadata" className="mt-6">
          <ProductMetaTab sku={sku} />
        </TabsContent>
      )}
    </Tabs>
  )
}
